import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    Tooltip,
    IconButton,
} from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import BusinessIcon from "@mui/icons-material/Business";
import { FC, useEffect, useState, FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { EmployeeUserType, UpdateEmployeeType } from "../../../types/employeeType";
import { StatusTypes } from "../../../types/common";
import { GroupItem } from "../../../types/groupItem";
import { placeholders } from "../utils";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { useEmployeesUsers } from "../../../store/selectors/employeesSelectors";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../../types/permissionType";
import { setResponseMessage, setResponseValue } from "../../../store/reducers/tableManagementReducer";
import { createNewEmployeeThunk, editEmployeeThunk } from "../../../store/thunk/employeesThunk";
import { mapThunkTypeToResponseValue } from "../../../common/logic/mapThunkTypeToResponseValue";

import MultiSelectGroups from "./MultiSelectGroups";
import SelectSubCompanyDialog from "./SubCompanyDialog";
import EmployeeModulesDialog from "./EmployeeModulesDialog";
import {
    CreateDialogFullWidthBox,
    CreateDialogGridContainer,
    TableSwitch,
} from "../Employees.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";

interface CreateOrEditEmployeeDialogProps {
    open: boolean;
    onClose: () => void;
    groupList: GroupItem[];
    mode: "create" | "edit";
    employee?: EmployeeUserType;
}

const REQUIRED_FIELDS: (keyof UpdateEmployeeType)[] = [
    "firstName",
    "lastName",
    "position",
    "employeeNumber",
];

type ErrorsType = {
    firstName: string;
    lastName: string;
    position: string;
    employeeNumber: string;
};

const initialErrors: ErrorsType = {
    firstName: "",
    lastName: "",
    position: "",
    employeeNumber: "",
};

const emptyEmployee: Partial<UpdateEmployeeType> = {
    firstName: "",
    lastName: "",
    position: "",
    employeeNumber: "",
    birthDate: undefined,
    employmentDate: undefined,
    status: StatusTypes.Active,
    groupIds: [],
    subCompanyId: null,
};

const CreateOrEditEmployeeDialog: FC<CreateOrEditEmployeeDialogProps> = ({
    open,
    onClose,
    groupList,
    mode,
    employee,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const existingEmployees = useEmployeesUsers();

    const [userData, setUserData] = useState<Partial<UpdateEmployeeType>>(emptyEmployee);
    const [errors, setErrors] = useState<ErrorsType>(initialErrors);
    const [modalMessage, setModalMessage] = useState("");
    const [openModulesDialog, setOpenModulesDialog] = useState(false);
    const [openSubCompanyDialog, setOpenSubCompanyDialog] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const { userHasPermission } = usePermissionChecker();

    const canOpenModules =
        mode === "edit" &&
        userHasPermission(PermissionEntityType.Employees, "Edit");

    useEffect(() => {
        if (!open) return;
        setUserData(mode === "edit" && employee ? employee : emptyEmployee);
        setErrors(initialErrors);
        setModalMessage("");
    }, [open, mode, employee]);

    if (mode === "edit" && !employee) {
        return null;
    }

    const hasMissingRequiredFields = REQUIRED_FIELDS.some(
        (field) => !userData[field]?.toString().trim()
    );
    const hasValidationErrors = Object.values(errors).some(Boolean);
    const isFormValid = !hasMissingRequiredFields && !hasValidationErrors;

    const validateUserData = (value: string, dataFileld: keyof UpdateEmployeeType) => {
        switch (dataFileld) {
            case "firstName":
                return value.length >= 2
            case "lastName":
                return value.length >= 2
            case "employeeNumber":
                return value.length <= 20 && value.length >= 1
            default:
                return true;
        }
    };

    const handleOnChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: keyof UpdateEmployeeType
    ) => {
        if (modalMessage) setModalMessage("");
        const value = e.target.value;
        setUserData((prev) => ({ ...prev, [field]: value }));
        if (errors[field] && validateUserData(value, field)) {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    const handleOnBlur = (
        e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: keyof UpdateEmployeeType
    ) => {
        const value = e.target.value.trim();
        if (!value) {
            setErrors((prev) => ({
                ...prev,
                [field]: t("authentication.register.required"),
            }));
            return;
        }
        setErrors((prev) => ({
            ...prev,
            [field]: validateUserData(value, field)
                ? ""
                : t("employeesPage.errors.notValid"),
        }));
    };

    const handleBirthDateChange = (date: Dayjs | null) =>
        setUserData((prev) => ({
            ...prev,
            birthDate: date ? date.format("YYYY-MM-DD") : undefined,
        }));

    const handleEmploymentDateChange = (date: Dayjs | null) =>
        setUserData((prev) => ({
            ...prev,
            employmentDate: date ? date.format("YYYY-MM-DD") : undefined,
        }));

    const handleUpdateGroupIds = (groupIds: number[]) =>
        setUserData((prev) => ({ ...prev, groupIds }));

    const handleSaveSubCompany = (subCompanyId: number | null) => {
        setUserData((prev) => ({ ...prev, subCompanyId }));
        setOpenSubCompanyDialog(false);
    };

    const handleSave = async () => {
        if (!isFormValid) {
            setModalMessage(t("dialogModals.fieldNotFilled"));
            return;
        }

        if (
            mode === "create" &&
            existingEmployees.some(
                (e) => e.employeeNumber === userData.employeeNumber
            )
        ) {
            setModalMessage(t("dialogModals.employeeNumberDuplication"));
            return;
        }

        try {
            setIsSaving(true);

            const employeePayload = userData as EmployeeUserType;
            const res =
                mode === "edit"
                    ? await dispatch(editEmployeeThunk(employeePayload))
                    : await dispatch(createNewEmployeeThunk(employeePayload));

            dispatch(setResponseValue(mapThunkTypeToResponseValue(res.type)));
            dispatch(
                setResponseMessage(
                    t(
                        mode === "edit"
                            ? "dialogModals.itemUpdatedUser"
                            : "dialogModals.itemCreatedUser"
                    )
                )
            );
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t(
                        mode === "edit"
                            ? "label.editUser"
                            : "label.addNewUser"
                    )}
                </DialogTitle>

                <DialogContent>
                    <CreateDialogGridContainer sx={{ pt: "1em" }}>
                        <TextField
                            fullWidth
                            label={`${t(placeholders.firstName)}*`}
                            value={userData.firstName}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            onChange={(e) => handleOnChange(e, "firstName")}
                            onBlur={(e) => handleOnBlur(e, "firstName")}
                        />
                        <TextField
                            fullWidth
                            label={`${t(placeholders.lastName)}*`}
                            value={userData.lastName}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            onChange={(e) => handleOnChange(e, "lastName")}
                            onBlur={(e) => handleOnBlur(e, "lastName")}
                        />

                        <DatePicker
                            label={t("employeesPage.placeholders.birthDate")}
                            value={userData.birthDate ? dayjs(userData.birthDate) : null}
                            onChange={handleBirthDateChange}
                            format="DD.MM.YYYY"
                        />
                        <DatePicker
                            label={t("employeesPage.placeholders.employmentDate")}
                            value={userData.employmentDate ? dayjs(userData.employmentDate) : null}
                            onChange={handleEmploymentDateChange}
                            format="DD.MM.YYYY"
                        />

                        <TextField
                            fullWidth
                            label={`${t(placeholders.position)}*`}
                            value={userData.position}
                            error={!!errors.position}
                            helperText={errors.position}
                            onChange={(e) => handleOnChange(e, "position")}
                            onBlur={(e) => handleOnBlur(e, "position")}
                        />
                        <TextField
                            fullWidth
                            label={`${t(placeholders.employeeNumber)}*`}
                            value={userData.employeeNumber}
                            error={!!errors.employeeNumber}
                            helperText={errors.employeeNumber}
                            onChange={(e) => handleOnChange(e, "employeeNumber")}
                            onBlur={(e) => handleOnBlur(e, "employeeNumber")}
                        />
                        {
                            mode === "create" && <TextField
                                fullWidth
                                type="password"
                                label={`${t("authentication.authSettings.password")}`}
                                value={userData.password}
                                onChange={(e) => handleOnChange(e, "password")}
                            />
                        }

                        <CreateDialogFullWidthBox>
                            <Typography fontWeight={600}>
                                {t("tableColumnHeaders.groups")}
                            </Typography>
                            <MultiSelectGroups
                                selectedGroupIds={userData.groupIds ?? []}
                                groupList={groupList}
                                handleUpdate={handleUpdateGroupIds}
                            />
                        </CreateDialogFullWidthBox>

                        <CreateDialogFullWidthBox>
                            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography>
                                        {userData.subCompanyId
                                            ? t("employeesPage.subCompanyAssigment.selected")
                                            : t("employeesPage.subCompanyAssigment.noSubCompany")}
                                    </Typography>
                                    <Tooltip title={t("employeesPage.subCompanyAssigment.selectSubCompany")}>
                                        <IconButton onClick={() => setOpenSubCompanyDialog(true)}>
                                            <BusinessIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <TableSwitch
                                        checked={userData.status === StatusTypes.Active}
                                        onChange={() => {
                                            const newStatus =
                                                userData.status === StatusTypes.Active
                                                    ? StatusTypes.Blocked
                                                    : StatusTypes.Active;
                                            setUserData((prev) => ({ ...prev, status: newStatus }));
                                        }}
                                        inputProps={{ "aria-label": "employee-status-switch" }}
                                    />
                                    <Typography variant="caption">
                                        {userData.status === StatusTypes.Active
                                            ? t("status.active")
                                            : t("status.blocked")}
                                    </Typography>
                                </Box>
                            </Box>
                        </CreateDialogFullWidthBox>
                    </CreateDialogGridContainer>

                    {canOpenModules && (
                        <Box mb={2}>
                            <Button
                                startIcon={<ListAltIcon />}
                                onClick={() => setOpenModulesDialog(true)}
                            >
                                {t("employeesPage.modules")}
                            </Button>
                        </Box>
                    )}

                    {modalMessage && (
                        <Typography color="error" mt={2}>
                            {modalMessage}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <GeneralButton
                        appVariant="secondary"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        {t("buttons.cancel")}
                    </GeneralButton>

                    <Tooltip
                        title={
                            !isFormValid
                                ? t(
                                    "employeesPage.errors.requiredFieldsMissing"
                                )
                                : ""
                        }
                    >
                        <span>
                            <GeneralButton
                                appVariant="primary"
                                onClick={handleSave}
                                disabled={!isFormValid || isSaving}
                                loading={isSaving}
                            >
                                {t("buttons.save")}
                            </GeneralButton>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>

            <SelectSubCompanyDialog
                open={openSubCompanyDialog}
                initialSubCompanyId={userData.subCompanyId}
                onClose={() => setOpenSubCompanyDialog(false)}
                onSave={handleSaveSubCompany}
            />

            <Dialog
                open={openModulesDialog}
                onClose={() => setOpenModulesDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <EmployeeModulesDialog
                    open={openModulesDialog}
                    onClose={() => setOpenModulesDialog(false)}
                    userId={userData.id ?? ""}
                    userName={`${userData.firstName} ${userData.lastName}`}
                />
            </Dialog>
        </>
    );
};

export default CreateOrEditEmployeeDialog;
